import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import Container from "@/components/Container";
import Footer from "@/components/Footer";
import Nav from "@/components/Nav";
import { locumColors } from "@/theme";

const legalOnly = import.meta.env.VITE_LEGAL_ONLY === "true";

const sectionSx = { mt: 6 } as const;
const headingSx = { mb: 2, fontWeight: 700, letterSpacing: "-0.01em" } as const;
const bodySx = {
  color: locumColors.textSecondary,
  lineHeight: 1.7,
  mb: 2,
} as const;
const listSx = {
  color: locumColors.textSecondary,
  lineHeight: 1.7,
  pl: 3,
  mb: 2,
  "& li": { mb: 1 },
} as const;

export default function PrivacyPage() {
  return (
    <>
      {!legalOnly && <Nav />}
      <Box component="main" sx={{ py: { xs: 8, md: 12 }, bgcolor: locumColors.white }}>
        <Container sx={{ maxWidth: "820px !important" }}>
          <Typography
            variant="h2"
            sx={{ mb: 1, fontWeight: 700, letterSpacing: "-0.02em" }}
          >
            Privacy Policy
          </Typography>
          <Typography sx={{ color: locumColors.muted, mb: 2 }}>
            LocumPlus Pty Ltd
          </Typography>
          <Typography sx={{ color: locumColors.muted, mb: 6, fontSize: "0.9375rem" }}>
            Version 1.0 &nbsp;·&nbsp; Effective date: 23 April 2026
          </Typography>

          <Box sx={sectionSx}>
            <Typography variant="h4" sx={headingSx}>Introduction</Typography>
            <Typography sx={bodySx}>
              LocumPlus Pty Ltd (ABN: 76 681 712 318) and its related companies
              (together <strong>we</strong>, <strong>us</strong>, <strong>our</strong> and
              other similar expressions) are committed to protecting your personal privacy.
              This Privacy Policy tells you how we collect, store, use and disclose your
              personal information. We encourage you to read this policy carefully so that
              you understand our information handling practices.
            </Typography>
          </Box>

          <Box sx={sectionSx}>
            <Typography variant="h4" sx={headingSx}>Information we collect</Typography>
            <Typography sx={bodySx}>
              In order to provide our customers with our products and services, we may
              collect and use personal information about you and others. If we are not
              provided with all the personal information we request, we may not be able to
              supply our products and services.
            </Typography>
            <Typography sx={bodySx}>
              We collect different information depending on our interaction with you. For
              example, we operate a SaaS platform and collect the following information
              depending on your role on the platform:
            </Typography>
            <Typography sx={bodySx}>
              <strong>Talent (locum doctors):</strong> name, date of birth, email address,
              profile picture, residential address and general location, medical
              specialty, practitioner grade, professional qualifications and registration
              details (including AHPRA registration number and verification data returned
              by AHPRA), credential documents uploaded by you, availability preferences,
              and application history.
            </Typography>
            <Typography sx={bodySx}>
              <strong>Employer Associates (agency staff):</strong> name, email address,
              employer organisation details, your assigned role within your organisation,
              and subscription and billing information.
            </Typography>
            <Typography sx={bodySx}>
              <strong>All users:</strong> device information collected at registration and
              during use (including IP address, device identifiers, and connection
              metadata), session data, platform usage and interaction data, advertising
              identifiers, and session identifiers.
            </Typography>
            <Typography sx={bodySx}>
              We do not store credit card numbers on our systems. Payment processing is
              handled by a third-party provider.
            </Typography>
            <Typography sx={bodySx}>
              We do not normally collect or store &lsquo;sensitive information&rsquo;, as
              defined in the <em>Privacy Act 1988</em> (eg. information about ethnic
              origin, religious beliefs or health). However, where we are required to and
              it is practicable to do so, we will seek your consent before collecting your
              sensitive information and inform you of the purpose at the same time.
            </Typography>
          </Box>

          <Box sx={sectionSx}>
            <Typography variant="h4" sx={headingSx}>How we collect personal information</Typography>
            <Typography sx={bodySx}>
              Generally, we collect an individual&rsquo;s personal information from that
              individual. For example via registration and signup forms, document uploads
              during onboarding, email messages sent to your platform-provisioned inbound
              email address (which we use for job ingestion), and your ongoing use of the
              platform.
            </Typography>
            <Typography sx={bodySx}>
              We collect device information (including IP address, device identifiers, and
              connection metadata) from all users at the time of registration and during
              your use of the platform. This applies to Talent (locum doctors), Employer
              Associates, and recruiters.
            </Typography>
            <Typography sx={bodySx}>
              We also collect and process personal information via our email ingestion
              pipeline. Employer Associates and their clients send job opportunity emails
              to a dedicated Locum Plus address for automated processing and job
              extraction.
            </Typography>
          </Box>

          <Box sx={sectionSx}>
            <Typography variant="h4" sx={headingSx}>How we store personal information</Typography>
            <Typography sx={bodySx}>
              We take all reasonable steps to keep secure any personal information which
              we hold about you and to protect your personal information from loss, misuse
              or unauthorised alteration. Any personal information you provide to us is
              stored on secure servers. Access to personal information is limited to those
              of our personnel who specifically need it to carry out their business
              responsibilities. We also maintain physical security procedures to manage
              and protect the use and storage of records containing personal information.
              Your data is hosted on Microsoft Azure cloud infrastructure. We maintain
              strict access controls, role-based permissions, and data isolation between
              employer organisations. Data belonging to one employer is not accessible to
              another employer on the platform.
            </Typography>
            <Typography sx={bodySx}>
              Where personal information is processed by artificial intelligence or
              machine learning models, we take reasonable steps to remove or de-identify
              personal information prior to processing. However, we cannot guarantee with
              complete certainty that all personal information will be removed before data
              is sent to these models, including models operated by third-party providers.
              You should be aware that residual personal information may on occasion be
              processed by these systems.
            </Typography>
            <Typography sx={bodySx}>
              Our employees and contractors are obliged and trained to respect the
              confidentiality of any personal information held by us. We implement
              appropriate technical and organisational measures, including encryption and
              access controls, to protect personal information from unauthorised access,
              disclosure, or loss. To help us protect your privacy, you should maintain
              the secrecy of the access credentials (ie. user name and password) you use
              to access and use our website.
            </Typography>
          </Box>

          <Box sx={sectionSx}>
            <Typography variant="h4" sx={headingSx}>Purpose of collection</Typography>
            <Typography sx={bodySx}>We collect personal information so we can:</Typography>
            <Box component="ul" sx={listSx}>
              <li>identify our customers, potential customers and their representatives;</li>
              <li>inform you or others of any initiatives we think may be of interest to you or them;</li>
              <li>inform you or others about our products and services and the benefits of using our products and services;</li>
              <li>provide you or others with information about offers or other benefits that may become available;</li>
              <li>seek your opinion or comments about our products and services;</li>
              <li>carry out billing and/or debt recovery activities;</li>
              <li>carry out our management, administrative quality assurance and complaint handling activities in a professional and efficient manner;</li>
              <li>develop and implement initiatives to improve our products and services;</li>
              <li>match Talent with suitable locum job opportunities based on their profile and preferences;</li>
              <li>verify professional credentials and qualifications;</li>
              <li>process job listings submitted by Employer Associates via email ingestion;</li>
              <li>manage subscriptions, billing, and platform access; and</li>
              <li>conduct targeted digital advertising and marketing campaigns, including on third-party platforms.</li>
            </Box>
          </Box>

          <Box sx={sectionSx}>
            <Typography variant="h4" sx={headingSx}>Use and Disclosure</Typography>
            <Typography sx={bodySx}>
              We use and disclose the personal information we collect for the purposes
              indicated above, and for other secondary purposes.
            </Typography>
            <Typography sx={bodySx}>
              We usually disclose the personal information we collect to our related
              entities, service providers and contractors, who help us supply our products
              and services. These include our cloud infrastructure provider (Microsoft
              Azure), and AI service providers used for document parsing and job data
              extraction, including Anthropic, OpenAI, and Mistral, and our third-party
              digital advertising platforms and identity resolution providers for the
              purpose of delivering targeted advertising on our behalf, building audience
              segments, and measuring campaign effectiveness.
            </Typography>
            <Typography sx={bodySx}>
              Except where indicated above, we will not use or disclose personal
              information unless:
            </Typography>
            <Box component="ul" sx={listSx}>
              <li>the individual concerned has consented to the use and disclosure;</li>
              <li>the third party is our service provider or contractor, in which case we will require them to use and disclose the personal information only for the purpose for which it was provided to them;</li>
              <li>the third party is a person involved in a dealing or proposed dealing (including a sale) of all or part of our assets and business;</li>
              <li>the third party is a credit reporting agency, your creditor, banker, financier, credit provider, mortgage insurer or trade insurer;</li>
              <li>the disclosure is to a related body corporate; or</li>
              <li>the disclosure is permitted, required or authorised by or under law.</li>
            </Box>
          </Box>

          <Box sx={sectionSx}>
            <Typography variant="h4" sx={headingSx}>Marketing</Typography>
            <Typography sx={bodySx}>
              We may use personal information to contact you about our products, services,
              and platform updates that we think may be of interest to you. This may
              include email communications, platform notifications, and targeted
              advertising delivered via third-party digital advertising platforms.
            </Typography>
            <Typography sx={bodySx}>
              If you no longer wish to receive direct marketing communications from us,
              you may opt out at any time by contacting our Privacy Officer using the
              details at the end of this policy, or by using the unsubscribe mechanism in
              any marketing email we send you.
            </Typography>
            <Typography sx={bodySx}>
              We do not provide your personal information to third parties for the purpose
              of allowing them to direct market their own products or services to you.
            </Typography>
          </Box>

          <Box sx={sectionSx}>
            <Typography variant="h4" sx={headingSx}>Access</Typography>
            <Typography sx={bodySx}>
              If at any time you want to know what personal information we hold about you,
              you are welcome to request access to that information by contacting us,
              using the details listed below. However, where:
            </Typography>
            <Box component="ul" sx={listSx}>
              <li>the access impacts on the privacy of others;</li>
              <li>the request for access is frivolous or vexatious;</li>
              <li>there are existing or anticipated legal proceedings; or</li>
              <li>such access can be denied under law or by a law enforcement agency,</li>
            </Box>
            <Typography sx={bodySx}>we may deny your request for access.</Typography>
            <Typography sx={bodySx}>
              If we deny your request for access, we will let you know why.
            </Typography>
            <Typography sx={bodySx}>
              We may also charge a fee to cover the reasonable costs we incur in
              processing your request.
            </Typography>
          </Box>

          <Box sx={sectionSx}>
            <Typography variant="h4" sx={headingSx}>Quality &amp; correction</Typography>
            <Typography sx={bodySx}>
              At all times we strive to ensure that the information we hold about you is
              accurate, complete and up-to-date. If at any time you believe the personal
              information that we hold about you is incorrect, incomplete or inaccurate,
              you should let us know. We will use all reasonable efforts to correct the
              information.
            </Typography>
          </Box>

          <Box sx={sectionSx}>
            <Typography variant="h4" sx={headingSx}>Website &amp; cookies</Typography>
            <Typography sx={bodySx}>
              We use cookies and similar tracking technologies on our website and
              platform. This includes first-party cookies for session management and
              platform functionality, and third-party tracking technologies such as
              advertising pixels, conversion tags, and server-side event APIs operated by
              digital advertising platforms.
            </Typography>
            <Typography sx={bodySx}>
              Some of these technologies may collect or link your activity to personal
              information held by us or by third-party advertising platforms, and may be
              used to identify you across devices and channels. Third-party advertising
              platforms that operate these technologies are governed by their own privacy
              policies.
            </Typography>
            <Typography sx={bodySx}>
              You can manage your cookie and tracking preferences through your browser
              settings. Please note that disabling certain tracking technologies may
              affect the functionality of our platform and the relevance of advertising
              you see.
            </Typography>
          </Box>

          <Box sx={sectionSx}>
            <Typography variant="h4" sx={headingSx}>Changes to our Privacy Policy</Typography>
            <Typography sx={bodySx}>
              As our business evolves our business processes and policies will be reviewed
              and may be revised. We may change our Privacy Policy at any time. We will
              notify you of any change by posting an updated version of our Privacy Policy
              on our website.
            </Typography>
          </Box>

          <Box sx={sectionSx}>
            <Typography variant="h4" sx={headingSx}>Complaints</Typography>
            <Typography sx={bodySx}>
              We are committed to constantly improving our procedures so that personal
              information is treated appropriately. If you feel that we have failed to
              deal with your personal information in accordance with this policy, please
              contact us, using the details listed below, so we have an opportunity to
              resolve the issue to your satisfaction.
            </Typography>
            <Typography sx={bodySx}>
              We will log your complaint and our privacy officer will:
            </Typography>
            <Box component="ul" sx={listSx}>
              <li>listen to your concerns and grievances;</li>
              <li>discuss with you the ways in which we can remedy the situation; and</li>
              <li>put in place an action plan to resolve your complaint and improve our information handling procedures if appropriate.</li>
            </Box>
            <Typography sx={bodySx}>
              You can make a complaint with the Office of the Australian Information
              Commissioner by phone at 1300 363 992, online at{" "}
              <Link
                href="https://www.oaic.gov.au/privacy/privacy-complaints"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: locumColors.tealDark, fontWeight: 600 }}
              >
                oaic.gov.au/privacy/privacy-complaints
              </Link>
              , or by post to: Office of the Australian Information Commissioner, GPO Box
              5218, Sydney NSW 2001.
            </Typography>
          </Box>

          <Box sx={sectionSx}>
            <Typography variant="h4" sx={headingSx}>Disclosure to overseas recipients</Typography>
            <Typography sx={bodySx}>
              We disclose personal information to recipients located overseas as part of
              our platform operations. These recipients include:
            </Typography>
            <Box component="ul" sx={listSx}>
              <li><strong>United States:</strong> Anthropic (AI processing), OpenAI (AI processing), Microsoft Azure (cloud infrastructure).</li>
              <li><strong>France:</strong> Mistral AI (AI processing).</li>
              <li><strong>European Union:</strong> specialist machine learning and AI contractors engaged under confidentiality agreements as part of our platform development.</li>
            </Box>
            <Typography sx={bodySx}>
              We take reasonable steps to ensure these overseas recipients handle your
              personal information in a manner consistent with the Australian Privacy
              Principles. By using the platform or any other services we provide, you
              consent to your personal information being disclosed to these overseas
              recipients.
            </Typography>
          </Box>

          <Box
            sx={{
              mt: 8,
              p: { xs: 3, md: 4 },
              bgcolor: locumColors.tealLight,
              borderRadius: "8px",
              border: `1px solid ${locumColors.tealBorderSubtle}`,
            }}
          >
            <Typography variant="h4" sx={headingSx}>Contact us</Typography>
            <Typography sx={bodySx}>
              If you require more detailed information about our information handling
              practices or if you have any concerns about our handling of your personal
              information, please let us know by contacting us at:
            </Typography>
            <Typography sx={{ color: locumColors.textPrimary, lineHeight: 1.8, mb: 0 }}>
              <strong>Privacy Officer</strong>
              <br />
              LocumPlus Pty Ltd
              <br />
              Email:{" "}
              <Link
                href="mailto:privacy@locumgroup.co"
                sx={{ color: locumColors.tealDark, fontWeight: 600 }}
              >
                privacy@locumgroup.co
              </Link>
            </Typography>
            <Typography sx={{ ...bodySx, mt: 2, mb: 0 }}>
              We will endeavour to respond to your concerns as quickly as possible.
            </Typography>
          </Box>
        </Container>
      </Box>
      {!legalOnly && <Footer />}
    </>
  );
}
